/**
 * useLocalSidebarOrder.ts
 *
 * Live-querying hook for sidebar order state.
 * Re-renders automatically when the sidebarOrder record changes in IndexedDB.
 *
 * Replaces: useRootPagesOrder() from rootPagesOrderContext
 *
 * Usage:
 *   const { privatePagesOrder, publicPagesOrder, workAreaPagesOrder, ... } = useLocalSidebarOrder()
 */

"use client";
import { useMemo } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { appDB } from "../db/client";
import type { SidebarOrderRecord } from "../db/schema";

// Default empty state — same shape as rootPagesOrderContext initial state
const DEFAULT: SidebarOrderRecord = {
  id: "global",
  private: [],
  public: [],
  workArea: {},
  shared: [],
  template: [],
  favourites: [],
  sidebar: [],
  isSync: 1 as 1,
  localUpdatedAt: 0,
};

interface UseLocalSidebarOrderReturn {
  // Same field names as rootPagesOrderContext — drop-in replacement
  privatePagesOrder: string[];
  publicPagesOrder: string[];
  workAreaPagesOrder: Record<string, string[]>;
  sharedPagesOrder: string[];
  templatePagesOrder: string[];
  favouritePagesOrder: string[];
  sidebarOrder: string[];

  // The raw record (if you need sync metadata)
  record: SidebarOrderRecord;

  // Whether still loading from IDB on first mount
  isLoading: boolean;
}

/**
 * Returns the current sidebar order from IndexedDB, live-updating.
 * Field names match rootPagesOrderContext exactly for easy migration.
 *
 * @param workspaceId - The active workspace ID. If the stored record belongs
 *   to a different workspace (e.g. written by another browser tab), the hook
 *   returns empty arrays so the Sidebar falls back to the React context that
 *   is hydrated from the API response for the correct workspace.
 */
export function useLocalSidebarOrder(workspaceId: string): UseLocalSidebarOrderReturn {

  const record = useLiveQuery(
    async () => (await appDB.sidebarOrder.get("global")) ?? null,
    []
  );

  return useMemo(() => {
    // Treat the record as absent when:
    //   • IDB is still loading (record === undefined)
    //   • the record has no workspaceId (written before this field was added)
    //   • the record belongs to a different workspace (stale cross-tab data)
    const isValid =
      record !== undefined &&
      record !== null &&
      !!record.workspaceId &&
      record.workspaceId === workspaceId;

    const data = isValid ? record! : DEFAULT;
    return {
      privatePagesOrder: data.private,
      publicPagesOrder: data.public,
      workAreaPagesOrder: data.workArea,
      sharedPagesOrder: data.shared,
      templatePagesOrder: data.template,
      favouritePagesOrder: data.favourites ?? [],
      sidebarOrder: data.sidebar,
      record: data,
      isLoading: record === undefined,
    };
  }, [record, workspaceId]);
}
